import { useAccessibleOrganizations } from '@app/src/context/AccessibleOrganizationsContext'
import { useAccount } from '@app/src/context/AccountContext'
import { useSessionNavigation } from '@app/src/context/SessionNavigationContext'
import type { GenericOrganization } from '@app/src/types/organizations'
import { Box, CircularProgress, Divider, Stack } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import paths from '@app/src/wf-constants/paths'
import { matchPath, useLocation } from 'react-router-dom'
import MenuCollapseIcon from './Icons/MenuCollapseIcon'
import MenuExpandIcon from './Icons/MenuExpandIcon'
import NavigationItems from './NavigationItems/NavigationItems'
import { NAV_LIST_PX, RAIL_WIDTH_TRANSITION_MS, navHiddenControlSx, railWidthTransition } from './navItemStyles'
import OrganizationAndSolutionMenu from './OrganizationAndSolutionMenu'
import { SourcingPromoSlot, SourcingRecommendationNavigation } from './SourcingNavigation'
import { VerticalMenuProvider, useVerticalMenuPin } from './VerticalMenuContext'
import WorldfavorLogo from './WorldfavorLogo'

const MENU_WIDTH_EXPANDED = 280
const MENU_WIDTH_COLLAPSED = 64
const HOVER_EXPAND_DELAY = 400
const HOVER_COLLAPSE_DELAY = 500
const HEADER_HEIGHT = 64

const VerticalMenu = () => {
  const { isPinned, setIsPinned } = useVerticalMenuPin()
  const [isHoverExpanded, setIsHoverExpanded] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const lastMousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const openPopoverCountRef = useRef(0)
  const { account } = useAccount()
  const { isPending: isSessionNavigationPending } = useSessionNavigation()
  const { accessibleOrganizations } = useAccessibleOrganizations()
  const { pathname } = useLocation()
  const currentOrganizationSolutions = accessibleOrganizations.find(
    ao => ao?.organization?.id === account?.organization?.id,
  )
  const currentOrganization = currentOrganizationSolutions?.organization as GenericOrganization

  const hasSelectedOrg = Boolean(account?.organization?.id)
  const isOnSolutionSelector = pathname === paths.solutionSelector
  const isOnSourcingRecommendation = Boolean(matchPath(pathname, { path: paths.sourcingRecommendation }))
  const showEmptyShell = !hasSelectedOrg || isOnSolutionSelector
  const showFullMenu =
    hasSelectedOrg && !isOnSolutionSelector && Boolean(currentOrganization) && !isSessionNavigationPending
  const showLoadingBody = hasSelectedOrg && !isOnSolutionSelector && !showFullMenu

  const isExpanded = showEmptyShell ? false : isPinned || isHoverExpanded

  const [isStableExpanded, setIsStableExpanded] = useState(isExpanded)
  useEffect(() => {
    if (!isExpanded) {
      setIsStableExpanded(false)
      return
    }
    if (isPinned) {
      setIsStableExpanded(true)
      return
    }
    const t = setTimeout(() => setIsStableExpanded(true), RAIL_WIDTH_TRANSITION_MS)
    return () => clearTimeout(t)
  }, [isExpanded, isPinned])

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }, [])

  // Clear any pending hover timeout on unmount so its callback can't fire
  // setIsHoverExpanded on an unmounted component.
  useEffect(() => clearHoverTimer, [clearHoverTimer])

  const handleMouseEnter = useCallback(() => {
    if (showEmptyShell) return
    if (isPinned) return
    clearHoverTimer()
    hoverTimerRef.current = setTimeout(() => {
      setIsHoverExpanded(true)
    }, HOVER_EXPAND_DELAY)
  }, [showEmptyShell, isPinned, clearHoverTimer])

  const scheduleCollapse = useCallback(() => {
    clearHoverTimer()
    hoverTimerRef.current = setTimeout(() => {
      if (openPopoverCountRef.current > 0) return
      setIsHoverExpanded(false)
    }, HOVER_COLLAPSE_DELAY)
  }, [clearHoverTimer])

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      if (showEmptyShell) return
      if (isPinned) return
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY }
      scheduleCollapse()
    },
    [showEmptyShell, isPinned, scheduleCollapse],
  )

  useEffect(() => {
    const trackMouse = (e: MouseEvent) => {
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY }
    }
    document.addEventListener('mousemove', trackMouse)
    return () => document.removeEventListener('mousemove', trackMouse)
  }, [])

  const handlePopoverOpen = useCallback(() => {
    openPopoverCountRef.current += 1
    clearHoverTimer()
  }, [clearHoverTimer])

  const handlePopoverClose = useCallback(() => {
    openPopoverCountRef.current = Math.max(0, openPopoverCountRef.current - 1)
    requestAnimationFrame(() => {
      const rect = menuRef.current?.getBoundingClientRect()
      if (!rect) return
      const { x, y } = lastMousePositionRef.current
      const isInsideMenu = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      if (!isInsideMenu) {
        scheduleCollapse()
      }
    })
  }, [scheduleCollapse])

  const spacerWidth = showEmptyShell ? MENU_WIDTH_COLLAPSED : isPinned ? MENU_WIDTH_EXPANDED : MENU_WIDTH_COLLAPSED
  const railWidth = showEmptyShell ? MENU_WIDTH_COLLAPSED : isExpanded ? MENU_WIDTH_EXPANDED : MENU_WIDTH_COLLAPSED

  return (
    <VerticalMenuProvider
      isExpanded={isExpanded}
      isStableExpanded={isStableExpanded}
      onPopoverOpen={handlePopoverOpen}
      onPopoverClose={handlePopoverClose}
    >
      <Box
        sx={{
          minWidth: spacerWidth,
          width: spacerWidth,
          transition: railWidthTransition(isPinned),
          flexShrink: 0,
        }}
      />

      <Stack
        ref={menuRef}
        onMouseEnter={showEmptyShell ? undefined : handleMouseEnter}
        onMouseLeave={showEmptyShell ? undefined : handleMouseLeave}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: railWidth,
          height: '100vh',
          borderRight: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          transition: railWidthTransition(isExpanded),
          zIndex: theme => theme.zIndex.drawer,
          boxShadow: !showEmptyShell && !isPinned && isHoverExpanded ? 6 : 0,
          overflow: 'hidden',
        }}
      >
        {showEmptyShell ? (
          <>
            <Stack px={0} py={2.5} alignItems="center" justifyContent="flex-start">
              <WorldfavorLogo />
            </Stack>

            {hasSelectedOrg && (
              <Stack flexGrow={1} justifyContent="flex-end">
                <Divider />
                <Stack justifyContent="space-between" sx={{ cursor: 'pointer' }} p={1}>
                  <OrganizationAndSolutionMenu account={account} />
                </Stack>
              </Stack>
            )}
          </>
        ) : (
          <>
            <Stack px={2} sx={{ minHeight: HEADER_HEIGHT }} justifyContent="center">
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <WorldfavorLogo />
                {!isPinned && (
                  <MenuExpandIcon
                    sx={[{ cursor: 'pointer', color: 'text.primary', flexShrink: 0 }, navHiddenControlSx(isExpanded)]}
                    onClick={() => {
                      setIsPinned(true)
                      setIsHoverExpanded(false)
                    }}
                  />
                )}
                {isPinned && (
                  <MenuCollapseIcon
                    sx={{ cursor: 'pointer', color: 'text.primary', flexShrink: 0 }}
                    onClick={() => {
                      setIsPinned(false)
                      setIsHoverExpanded(false)
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {showLoadingBody ? (
              <Stack alignItems="center" justifyContent="center" flexGrow={1}>
                <CircularProgress size={24} />
              </Stack>
            ) : (
              <>
                <Stack px={NAV_LIST_PX} py={1} sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
                  {isOnSourcingRecommendation ? <SourcingRecommendationNavigation /> : <NavigationItems />}
                </Stack>

                {!isOnSourcingRecommendation && <SourcingPromoSlot />}

                <Stack flexGrow={0} justifyContent="flex-end">
                  <Divider />
                  <Stack justifyContent="space-between" sx={{ cursor: 'pointer' }} p={1}>
                    <OrganizationAndSolutionMenu account={account} />
                  </Stack>
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </VerticalMenuProvider>
  )
}

export default VerticalMenu
